// Durable "intended destination" breadcrumb for the auth flow.
//
// When a logged-out (or workspace-less) user is bounced to /login, we stash
// where they were trying to go here so we can return them after they sign in.
// Modeled on the pendingClaimToken / pendingInviteToken localStorage
// precedents (see Claim.js / AcceptInvite.js): localStorage (not React-Router
// state) so it survives full reloads and the Login<->SignUp footer hop, and
// survives the multi-step onboarding flow.
//
// Precedence on every auth-complete path is:
//   pendingClaimToken > pendingRedirectPath > pendingInviteToken > default

const PENDING_REDIRECT_KEY = 'pendingRedirectPath';

// Paths we must NEVER store, to avoid redirect loops and double-handling of
// token flows. Claim/invite have their own authoritative breadcrumbs
// (pendingClaimToken / pendingInviteToken) — storing /accept-invite here would
// double-fire. Auth + onboarding routes would bounce the user right back.
const isStorablePath = (path) => {
    if (typeof path !== 'string' || path === '') return false;
    // Internal paths only — must start with a single '/'. Rejects '//evil.com'
    // (protocol-relative), 'http://…', and empty strings (open-redirect guard).
    if (!path.startsWith('/') || path.startsWith('//')) return false;
    const pathOnly = path.split('?')[0].split('#')[0];
    if (pathOnly === '/login') return false;
    if (pathOnly === '/sign-up') return false;
    if (pathOnly === '/claim') return false;
    if (pathOnly === '/accept-invite') return false;
    if (pathOnly === '/onboarding' || pathOnly.startsWith('/onboarding/')) return false;
    return true;
};

// Store pathname + search so query params survive auth (e.g. /comps/123?tab=arv).
export const setPendingRedirect = (path) => {
    if (typeof window === 'undefined') return;
    if (!isStorablePath(path)) return; // silently refuse — no-op
    try { window.localStorage.setItem(PENDING_REDIRECT_KEY, path); }
    catch { /* ignore */ }
};

// Read + remove in one shot (matches the claim/invite consume pattern). Returns
// null if nothing valid is stored. Re-validates on read so a stale or
// hand-edited value can never produce a loop or off-site redirect.
export const consumePendingRedirect = () => {
    if (typeof window === 'undefined') return null;
    try {
        const value = window.localStorage.getItem(PENDING_REDIRECT_KEY);
        window.localStorage.removeItem(PENDING_REDIRECT_KEY);
        return isStorablePath(value) ? value : null;
    } catch { return null; }
};

// Discard without using it (new-user paths — they always land at '/').
export const clearPendingRedirect = () => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(PENDING_REDIRECT_KEY); }
    catch { /* ignore */ }
};
